import { useCallback, useEffect } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { mediaImage, optimizedImg } from "@/lib/media";

// items: array of media values (string url or media object). index: number|null. setIndex(null) closes.
export default function Lightbox({ items = [], index, setIndex, altPrefix = "Photo" }) {
  const count = items.length;
  const nav = useCallback((d) => setIndex((i) => (i + d + count) % count), [count, setIndex]);

  useEffect(() => {
    if (index === null || index === undefined) return;
    const onKey = (e) => {
      if (e.key === "Escape") setIndex(null);
      if (e.key === "ArrowRight") nav(1);
      if (e.key === "ArrowLeft") nav(-1);
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [index, nav, setIndex]);

  if (index === null || index === undefined || !count) return null;
  const src = mediaImage(items[index]);

  return (
    <div className="fixed inset-0 z-[90] bg-ink/95 flex items-center justify-center" onClick={() => setIndex(null)} data-testid="lightbox">
      <button aria-label="Close" className="absolute top-5 right-5 text-ivory/80 hover:text-ivory p-2 z-10" onClick={() => setIndex(null)} data-testid="lightbox-close">
        <X className="w-7 h-7" />
      </button>
      {count > 1 && (
        <>
          <button aria-label="Previous" className="absolute left-2 md:left-6 text-ivory/70 hover:text-ivory p-3 z-10" onClick={(e) => { e.stopPropagation(); nav(-1); }} data-testid="lightbox-prev">
            <ChevronLeft className="w-9 h-9" />
          </button>
          <button aria-label="Next" className="absolute right-2 md:right-6 text-ivory/70 hover:text-ivory p-3 z-10" onClick={(e) => { e.stopPropagation(); nav(1); }} data-testid="lightbox-next">
            <ChevronRight className="w-9 h-9" />
          </button>
        </>
      )}
      <img
        src={optimizedImg(src, 1600, 82)}
        alt={`${altPrefix} ${index + 1}`}
        className="max-h-[88vh] max-w-[92vw] object-contain select-none animate-fade-up"
        onClick={(e) => e.stopPropagation()}
        data-testid="lightbox-image"
      />
      {count > 1 && (
        <div className="absolute bottom-5 left-1/2 -translate-x-1/2 text-ivory/70 text-xs tracking-[0.2em] uppercase" data-testid="lightbox-counter">
          {index + 1} / {count}
        </div>
      )}
    </div>
  );
}
